import { LINK_TYPES } from '../config/entityTypeRegistry';

/**
 * Maps typed content-block reference types to target entity fields and link semantics.
 */
export const TYPED_REFERENCE_BLOCK_MAP = {
  day_reference: { idField: 'dayId', targetType: 'day', linkType: LINK_TYPES.ASSOCIATED_WITH },
  place_reference: { idField: 'placeId', targetType: 'place', linkType: LINK_TYPES.ASSOCIATED_WITH },
  accommodation_reference: {
    idField: 'accommodationId',
    targetType: 'accommodation',
    linkType: LINK_TYPES.ASSOCIATED_WITH
  },
  travel_reference: { idField: 'travelId', targetType: 'travel', linkType: LINK_TYPES.ASSOCIATED_WITH },
  document_reference: { idField: 'documentId', targetType: 'document', linkType: LINK_TYPES.DOCUMENT_FOR }
};

/**
 * Returns true when a block is handled by a more specific extractor.
 * @param {string} hostEntityType
 * @param {Object} block
 */
export function shouldSkipTypedReferenceBlock(hostEntityType, block) {
  if (!block?.type) {
    return true;
  }

  // Place inverse accommodation blocks use sourceEntityType, not place_reference.
  if (block.sourceEntityType && block.sourceEntityId) {
    return true;
  }

  // Day itinerary accommodation refs are stays_on, not associated_with.
  if (hostEntityType === 'day' && block.type === 'accommodation_reference') {
    return true;
  }

  // Accommodation itinerary day refs are stays_on, not associated_with.
  if (hostEntityType === 'accommodation' && block.type === 'day_reference') {
    return true;
  }

  return !TYPED_REFERENCE_BLOCK_MAP[block.type];
}

/**
 * Extracts link candidates from typed entity reference blocks (entityLinkingService output).
 * @param {string} hostEntityType
 * @param {string} hostEntityId
 * @param {Array} blocks
 * @param {Map} map
 * @param {Function} addCandidate
 */
export function extractFromTypedReferenceBlocks(hostEntityType, hostEntityId, blocks, map, addCandidate) {
  if (!Array.isArray(blocks)) {
    return;
  }

  blocks.forEach((block) => {
    if (shouldSkipTypedReferenceBlock(hostEntityType, block)) {
      return;
    }

    const config = TYPED_REFERENCE_BLOCK_MAP[block.type];
    const targetId = block[config.idField];
    if (!targetId) {
      return;
    }

    if (hostEntityType === config.targetType && hostEntityId === targetId) {
      return;
    }

    addCandidate(
      map,
      { type: hostEntityType, id: hostEntityId },
      { type: config.targetType, id: targetId },
      config.linkType,
      {
        linkIntent: 'selected',
        displayMode: 'related_only',
        displayContext: block.context || null
      },
      { legacySource: 'manual_linker', createdBy: 'extractor' }
    );
  });
}

/**
 * Returns true for legacy accommodation→day metadata blocks (untyped, with dayId).
 * @param {Object} block
 */
export function isLegacyAccommodationDayBlock(block) {
  return Boolean(block?.dayId && !block.type);
}

/**
 * Returns true for accommodation→day itinerary blocks (legacy or typed day_reference).
 * @param {Object} block
 */
export function isAccommodationDayItineraryBlock(block) {
  if (!block?.dayId) {
    return false;
  }
  return isLegacyAccommodationDayBlock(block) || block.type === 'day_reference';
}
